import { z } from 'zod'
import {
  adminSectionToggleCode,
  ENTITLEMENT_ADMIN_SECTION_IDS,
  ENTITLEMENT_MODULE_IDS,
  moduleToggleCode,
  type FeatureToggleKind,
} from './feature-toggle.ts'

/**
 * Catalogos configuraveis.
 *
 * Regra estrutural da spec (secoes 13.1 e 18.3/18.4): tipos, status, prioridades
 * e tipos de relacao NAO sao enums fechados. A instalacao e semeada com um
 * conjunto inicial, mas o administrador cadastra novos a qualquer momento. Nada
 * na UI ou nas regras pode depender de um valor especifico existir.
 *
 * Os codigos abaixo sao apenas os SEEDS — servem para a migration inicial e para
 * regras que precisam de ancora semantica (ex.: ACCEPTANCE_CRITERION bloqueia a
 * conclusao do item pai). Codigos semeados sao marcados `isSystem` e nao podem
 * ser excluidos, so desativados.
 */

/** Semantica visual de um item de catalogo — dirige cor de badge/coluna. */
export const toneSchema = z.enum(['neutral', 'info', 'primary', 'success', 'warning', 'danger'])
export type Tone = z.infer<typeof toneSchema>

/** Campos comuns a todo item de catalogo configuravel. */
export const catalogEntrySchema = z.object({
  id: z.string().uuid(),
  /** Codigo tecnico, unico e imutavel depois de usado. */
  code: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[A-Z][A-Z0-9_]*$/, 'use SCREAMING_SNAKE_CASE'),
  /** Chave i18n do nome. O nome exibido vem sempre da tabela de traducao. */
  nameTranslationKey: z.string().min(1),
  descriptionTranslationKey: z.string().nullable(),
  icon: z.string().nullable(),
  tone: toneSchema,
  color: z
    .string()
    .regex(/^#[0-9A-F]{6}$/i)
    .nullable(),
  /** Semeado pelo sistema: nao pode ser excluido nem ter o codigo alterado. */
  isSystem: z.boolean(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
})
export type CatalogEntry = z.infer<typeof catalogEntrySchema>

// --- Seeds --------------------------------------------------------------

/** Tipos de Item de Trabalho semeados (spec 13.1). */
export const SEEDED_WORK_ITEM_TYPES = [
  { code: 'ACTIVITY', tone: 'neutral', icon: 'fas fa-layer-group', sortOrder: 10 },
  { code: 'TASK', tone: 'info', icon: 'fas fa-tasks', sortOrder: 20 },
  { code: 'STORY', tone: 'primary', icon: 'fas fa-book', sortOrder: 30 },
  { code: 'ACCEPTANCE_CRITERION', tone: 'success', icon: 'fas fa-clipboard-check', sortOrder: 40 },
  { code: 'BUG', tone: 'danger', icon: 'fas fa-bug', sortOrder: 50 },
  { code: 'APPROVAL', tone: 'warning', icon: 'fas fa-user-check', sortOrder: 60 },
] as const satisfies readonly { code: string; tone: Tone; icon: string; sortOrder: number }[]

/**
 * Codigo com significado estrutural: um item deste tipo, quando relacionado via
 * ACCEPTANCE_CRITERION_OF e marcado como obrigatorio, bloqueia a conclusao do
 * item alvo. E o unico tipo que o motor de regras conhece pelo codigo.
 */
export const ACCEPTANCE_CRITERION_TYPE_CODE = 'ACCEPTANCE_CRITERION'

/**
 * Status semeados (spec 13.8). Status sao configuraveis e mapeados por Kanban.
 *
 * Sao apenas o PONTO DE PARTIDA de um workspace novo: nao existe mais status
 * "do sistema" — todos podem ser renomeados, reordenados e excluidos
 * (pedido 2026-08-29).
 */
export const SEEDED_WORK_ITEM_STATUSES = [
  { code: 'PENDING', tone: 'neutral', isInitial: true, isTerminal: false, sortOrder: 10 },
  { code: 'IN_PROGRESS', tone: 'primary', isInitial: false, isTerminal: false, sortOrder: 20 },
  { code: 'DONE', tone: 'success', isInitial: false, isTerminal: true, sortOrder: 30 },
  { code: 'BLOCKED', tone: 'danger', isInitial: false, isTerminal: false, sortOrder: 40 },
] as const satisfies readonly {
  code: string
  tone: Tone
  isInitial: boolean
  isTerminal: boolean
  sortOrder: number
}[]

/** Prioridades semeadas (spec 22.2). */
export const SEEDED_PRIORITIES = [
  { code: 'LOW', tone: 'neutral', sortOrder: 10 },
  { code: 'MEDIUM', tone: 'info', sortOrder: 20 },
  { code: 'HIGH', tone: 'warning', sortOrder: 30 },
  { code: 'URGENT', tone: 'danger', sortOrder: 40 },
] as const satisfies readonly { code: string; tone: Tone; sortOrder: number }[]

/**
 * Tipos de relacao semeados (spec 13.7).
 *
 * Toda relacao tem label direto e reverso — a UI precisa apresentar o vinculo
 * corretamente dos dois lados. `isSymmetric` marca os casos em que direto e
 * reverso sao o mesmo texto.
 */
export const SEEDED_RELATION_TYPES = [
  { code: 'RELATED_TO', isSymmetric: true, sortOrder: 10 },
  { code: 'BLOCKS', isSymmetric: false, sortOrder: 20 },
  { code: 'DEPENDS_ON', isSymmetric: false, sortOrder: 30 },
  { code: 'DUPLICATES', isSymmetric: false, sortOrder: 40 },
  { code: 'IMPLEMENTED_BY', isSymmetric: false, sortOrder: 50 },
  { code: 'ACCEPTANCE_CRITERION_OF', isSymmetric: false, sortOrder: 60 },
] as const satisfies readonly { code: string; isSymmetric: boolean; sortOrder: number }[]

/**
 * Relacao que liga um criterio de aceite ao item que ele qualifica.
 * A origem e o criterio; o destino e o item principal.
 */
export const ACCEPTANCE_CRITERION_RELATION_CODE = 'ACCEPTANCE_CRITERION_OF'

/**
 * Kill-switch da busca de icones na internet (Iconify): dependencia externa —
 * se o provedor cair ou responder lixo, o admin desliga sem deploy.
 * O guard `requireToggle` na API e a aba do IconPicker apontam para este codigo.
 */
export const CATALOG_ICON_WEB_SEARCH_TOGGLE_CODE = 'CATALOG_ICON_WEB_SEARCH'

/**
 * Feature toggles semeados (SPEC-20260814-1144). Marcados `isSystem` (nao podem
 * ser excluidos/arquivados nem ter o codigo alterado). O valor `enabled` aqui e
 * so o estado INICIAL: o seed usa onConflictDoNothing para nunca religar o que
 * um admin desligou em producao. Regras e schemas em `./feature-toggle.ts`.
 */
export const SEEDED_FEATURE_TOGGLES = [
  {
    code: CATALOG_ICON_WEB_SEARCH_TOGGLE_CODE,
    kind: 'OPS',
    enabled: true,
    description: 'Busca de ícones na internet (Iconify) no seletor de ícones dos catálogos.',
  },
  /**
   * Contatos de outras empresas no chat (2026-09-13). RELEASE: está em
   * avaliação e pode ser removido. A migration 0102 semeia produção.
   */
  {
    code: 'CHAT_EXTERNAL_CONTACTS',
    kind: 'RELEASE',
    enabled: true,
    description:
      'Comunicação: buscar e convidar pessoas de outras empresas do Orbind para conversar. Em avaliação — desligue para voltar a só pessoas do workspace.',
  },
  /**
   * Um toggle ENTITLEMENT por modulo vendavel do shell (SPEC-20260814-1333).
   * `enabled: true` = master ligado; quem corta por empresa e o plano do
   * workspace (ou a excecao por workspace), nunca o seed.
   */
  ...ENTITLEMENT_MODULE_IDS.map((moduleId) => ({
    code: moduleToggleCode(moduleId),
    kind: 'ENTITLEMENT' as const,
    enabled: true,
    description: `Módulo ${moduleId} disponível para o workspace conforme o plano contratado.`,
  })),
  /**
   * Uma tela da Administracao por toggle (SPEC-20260814-1427). Feature toggles
   * e Planos ficam fora de proposito — anti-lockout.
   */
  ...ENTITLEMENT_ADMIN_SECTION_IDS.map((sectionId) => ({
    code: adminSectionToggleCode(sectionId),
    kind: 'ENTITLEMENT' as const,
    enabled: true,
    description: `Tela de administração ${sectionId} disponível conforme o plano contratado.`,
  })),
] satisfies readonly { code: string; kind: FeatureToggleKind; enabled: boolean; description: string }[]
